import React, { type MouseEvent } from "react";
import DrawingLine from "./DrawingLine";
import { type Line, type Vector2D } from "./models";

interface Props {
  className?: string
  style?: React.CSSProperties
  lines: Line[]
  onLinesChanged: (newLines: Line[]) => void
  svgRef: React.LegacyRef<SVGSVGElement>
}

interface State {
  isDrawing: boolean
}

export default class DrawingCanvas extends React.Component<React.PropsWithChildren<Props>, State> {
  state: State = {
    isDrawing: false
  };

  drawingAreaRef = React.createRef<HTMLDivElement>();

  componentDidMount() {
    document.addEventListener("mouseup", this.handleMouseUp);
    document.addEventListener("touchend", this.handleMouseUp);
  }

  componentWillUnmount() {
    document.removeEventListener("mouseup", this.handleMouseUp);
    document.removeEventListener("touchend", this.handleMouseUp);
  }

  handleMouseDown = (mouseEvent: MouseEvent<HTMLDivElement>) => {
    if (mouseEvent.button !== 0) {
      return;
    }

    this.handleInitialPress(mouseEvent.clientX, mouseEvent.clientY);
  };

  handleTouchStart = (touchEvent: React.TouchEvent<HTMLDivElement>) => {
    this.handleInitialPress(touchEvent.touches[0].clientX, touchEvent.touches[0].clientY);
  };

  handleInitialPress = (clientX: number, clientY: number) => {
    const point = this.relativeCoordinatesForEvent(clientX, clientY);
    const newLines: Line[] = [...this.props.lines, { points: [point] }];
    this.setState({
      isDrawing: true
    });
    this.props.onLinesChanged(newLines);
    document.addEventListener("dragstart", this.stopDraggingBehavior);
  };

  handleMouseMove = (mouseEvent: MouseEvent<HTMLDivElement>) => {
    if (!this.state.isDrawing) {
      return;
    }
    this.handleMovement(mouseEvent.clientX, mouseEvent.clientY);
  };

  handleTouchMove = (touchEvent: React.TouchEvent<HTMLDivElement>) => {
    if (!this.state.isDrawing) {
      return;
    }
    this.handleMovement(touchEvent.touches[0].clientX, touchEvent.touches[0].clientY);
  };

  handleMovement = (clientX: number, clientY: number) => {
    const point = this.relativeCoordinatesForEvent(clientX, clientY);

    const lastLine: Line = this.props.lines[this.props.lines.length - 1];
    const newLine: Line = { points: [...lastLine.points, point] };
    const lastIndex = this.props.lines.length - 1;
    const newLines: Line[] = Object.assign([], this.props.lines, { [lastIndex]: newLine });

    this.props.onLinesChanged(newLines);
  };

  stopDraggingBehavior = (e: DragEvent) => {
    e.preventDefault();
  };

  handleMouseUp = () => {
    this.setState({ isDrawing: false });
    document.removeEventListener("dragstart", this.stopDraggingBehavior);
  };

  relativeCoordinatesForEvent = (clientX: number, clientY: number): Vector2D => {
    const boundingRect = this.drawingAreaRef.current.getBoundingClientRect();
    return {
      x: clientX - boundingRect.left,
      y: clientY - boundingRect.top
    };
  };

  public render(): JSX.Element {
    return <div
      ref={this.drawingAreaRef}
      onMouseDown={this.handleMouseDown}
      onMouseMove={this.handleMouseMove}
      onTouchStart={this.handleTouchStart}
      onTouchMove={this.handleTouchMove}
      style={{
        touchAction: "none",
        ...this.props.style
      }}
      className={this.props.className}
    >
      {this.props.children}
      <svg
        ref={this.props.svgRef}
        style={{ width: "100%", height: "100%" }} className="drawing"
      >
        {this.props.lines.map((line, index) => (
          <DrawingLine key={index} line={line} />
        ))}
      </svg>
    </div>;
  }
}
